import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceArea,
  ReferenceDot,
  Label,
  ReferenceLine,
} from 'recharts';
import {
  AGE,
  CHART_KEY_NUMERICAL_CONSTS,
  CHART_STRINGS,
} from './helpers/graphConstants';
import { getDialysisInfo } from '../../../utils/dialysisModel/dialysisModel';
import '../../../../assets/stylesheets/components/chart.scss';
import CenteredYAxisLabel from './components/CenteredYAxisLabel';
import ApproximateDialysisLabel from './components/ApproximateDialysisLabel';
import CustomLegend from './components/CustomLegend';
import TreatmentInitiationLabel from './components/TreatmentInitiationLabel';
import ProlongedDialysisLabel from './components/ProlongedDialysisLabel';
import CustomXAxisTick from './components/CustomXAxisTick';
import CustomYAxisTick from './components/CustomYAxisTick';
import CustomTooltip from './components/CustomTooltip';
import useWindowSize from '../../../hooks/useWindowSize';

const getChartAxisInfo = (treatmentDialysis, egfrInitial, initialAge) => {
  return {
    xDivisionSize: 5,
    yDivisionSize: 10,
    xMinTicks: Math.floor(initialAge / 5) * 5,
    xMaxTicks:
      initialAge >= CHART_KEY_NUMERICAL_CONSTS.MAX_X
        ? initialAge + 1
        : CHART_KEY_NUMERICAL_CONSTS.MAX_X,
    xMin: initialAge,
    xMax: treatmentDialysis,
    yMin: CHART_KEY_NUMERICAL_CONSTS.ORIGIN_Y,
    yMax: egfrInitial + CHART_KEY_NUMERICAL_CONSTS.MAX_Y_OFFSET,
  };
};

const generateChartData = (axisInfo, egfrInitial, dialysisInfo) => {
  const { xMin, xMaxTicks } = axisInfo;
  const xDivisionSize = 1;

  const slopeWithout =
    (CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET - egfrInitial) /
    (dialysisInfo.noTreatmentDialysisAge - xMin);

  const withoutTreatment = (age) => egfrInitial + slopeWithout * (age - xMin);

  const yAtInitiation = withoutTreatment(dialysisInfo.treatmentInitiated);

  const slopeWith =
    (CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET - yAtInitiation) /
    (dialysisInfo.treatmentDialysisAge - dialysisInfo.treatmentInitiated);

  const withTreatment = (age) => {
    if (age < dialysisInfo.treatmentInitiated || age > xMaxTicks) return null;
    return yAtInitiation + slopeWith * (age - dialysisInfo.treatmentInitiated);
  };

  const data = [];
  for (let age = xMin; age <= xMaxTicks; age += xDivisionSize) {
    const rawWt =
      age <= dialysisInfo.noTreatmentDialysisAge ? withoutTreatment(age) : null;
    const rawWti =
      age <= dialysisInfo.treatmentDialysisAge ? withTreatment(age) : null;

    const wt =
      rawWt !== null && rawWt >= CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET
        ? rawWt
        : null;
    const wti =
      rawWti !== null && rawWti >= CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET
        ? rawWti
        : null;

    let base = null;
    let height = null;
    if (wti !== null && wt !== null) {
      base = wt;
      height = wti - wt;
    } else if (wti !== null) {
      base = CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET;
      height = wti - CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET;
    }

    data.push({ age, withoutTreatment: wt, withTreatment: wti, base, height });
  }
  return data;
};

const DynamicChart = ({ egfrInitial, age, risk, treatment }) => {
  const dialysisInfo = getDialysisInfo(risk, treatment, age, egfrInitial);
  const axisInfo = getChartAxisInfo(
    dialysisInfo.treatmentDialysisAge,
    egfrInitial,
    age
  );

  const yAtInitiation = egfrInitial;

  const chartData = useMemo(
    () => generateChartData(axisInfo, egfrInitial, dialysisInfo),
    [age, axisInfo, egfrInitial, dialysisInfo]
  );

  const xTicks = useMemo(() => {
    const ticks = [];
    for (let x = 20; x <= 95; x += 5) {
      ticks.push(x);
    }
    return ticks;
  }, []);

  const yTicks = useMemo(() => {
    const ticks = [];
    for (
      let y = axisInfo.yMin;
      y <= axisInfo.yMax;
      y += axisInfo.yDivisionSize
    ) {
      ticks.push(y);
    }
    return ticks;
  }, [axisInfo]);

  const stageTicks = [
    CHART_KEY_NUMERICAL_CONSTS.STAGE_1,
    CHART_KEY_NUMERICAL_CONSTS.STAGE_2,
    CHART_KEY_NUMERICAL_CONSTS.STAGE_3A,
    CHART_KEY_NUMERICAL_CONSTS.STAGE_3B,
    CHART_KEY_NUMERICAL_CONSTS.STAGE_4,
    CHART_KEY_NUMERICAL_CONSTS.STAGE_5,
  ].filter((t) => t <= axisInfo.yMax);

  const stageFormatter = (val) => {
    switch (val) {
      case CHART_KEY_NUMERICAL_CONSTS.STAGE_1:
        return CHART_STRINGS.STAGE_1;
      case CHART_KEY_NUMERICAL_CONSTS.STAGE_2:
        return CHART_STRINGS.STAGE_2;
      case CHART_KEY_NUMERICAL_CONSTS.STAGE_3A:
        return CHART_STRINGS.STAGE_3A;
      case CHART_KEY_NUMERICAL_CONSTS.STAGE_3B:
        return CHART_STRINGS.STAGE_3B;
      case CHART_KEY_NUMERICAL_CONSTS.STAGE_4:
        return CHART_STRINGS.STAGE_4;
      case CHART_KEY_NUMERICAL_CONSTS.STAGE_5:
        return CHART_STRINGS.STAGE_5;
      default:
        return '';
    }
  };
  const MIN_WIDTH_PX = 450;
  const MAX_WIDTH_PX = 1024;
  const WIDTH_DOWNSCALE_FACTOR = 0.9;
  const CHART_ASPECT_RATIO = 0.6;

  const { width: windowWidth } = useWindowSize();
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const getResponsiveWidth = () => {
    if (!windowWidth) return MIN_WIDTH_PX;
    const preferred = windowWidth * WIDTH_DOWNSCALE_FACTOR;
    return clamp(preferred, MIN_WIDTH_PX, MAX_WIDTH_PX);
  };

  const chartWidth = getResponsiveWidth();
  const chartHeight = CHART_ASPECT_RATIO * chartWidth;

  return (
    <div
      className="dynamic-chart-wrapper"
      style={{
        width: `${chartWidth}px`,
        height: `${chartHeight}px`,
      }}
    >
      <ResponsiveContainer width="100%" height="100%" className="dynamic-chart">
        <ComposedChart
          data={chartData}
          margin={{ top: 30, right: 60, bottom: 20, left: 60 }}
        >
          <defs>
            <linearGradient id="backgroundGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F3F8FF" />
              <stop offset="40%" stopColor="#E0ECFD" />
              <stop offset="100%" stopColor="#87ADDD" />
            </linearGradient>
            <linearGradient id="timeGainedGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#FFDA7A" stopOpacity={0.15} />
              <stop offset="100%" stopColor="#FFDA7A" stopOpacity={1} />
            </linearGradient>
          </defs>

          <ReferenceArea
            yAxisId="left"
            x1={axisInfo.xMin}
            x2={axisInfo.xMaxTicks}
            y1={axisInfo.yMin}
            y2={axisInfo.yMax + 1}
            fill="url(#backgroundGradient)"
            fillOpacity={1.0}
            stroke="none"
            ifOverflow="visible"
          />

          <XAxis
            dataKey="age"
            domain={[AGE.MIN, AGE.MAX]}
            ticks={xTicks}
            tick={<CustomXAxisTick />}
            tickLine={{ stroke: '#00285B' }}
            axisLine={{ stroke: '#00285B' }}
            ifOverflow="visible"
            label={{
              value: CHART_STRINGS.X_LABEL,
              position: 'insideBottomLeft',
              offset: -20,
              className: 'axis-label',
            }}
          />

          {/* Left numeric Y-axis */}
          <YAxis
            yAxisId="left"
            orientation="left"
            domain={[axisInfo.yMin, axisInfo.yMax + 1]}
            ticks={yTicks}
            tick={<CustomYAxisTick />}
            tickLine={{ stroke: '#00285B' }}
            axisLine={{ stroke: '#00285B' }}
            label={
              <CenteredYAxisLabel className="axis-label">
                {CHART_STRINGS.Y_LABEL_LEFT}
              </CenteredYAxisLabel>
            }
          />

          {/* Right CKD-Stage Y-axis */}
          <YAxis
            yAxisId="right"
            orientation="right"
            domain={[axisInfo.yMin, axisInfo.yMax + 1]}
            ticks={stageTicks}
            tickFormatter={stageFormatter}
            label={{
              value: CHART_STRINGS.Y_LABEL_RIGHT,
              position: 'insideTopRight',
              offset: -40,
              className: 'right-axis-label',
            }}
            axisLine={false}
            tickLine={false}
          />

          {/* Invisible line for right Y-axis ticks */}
          <Line
            type="monotone"
            dataKey="withTreatment"
            yAxisId="right"
            stroke="transparent"
            dot={false}
            activeDot={false}
          />

          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="middle"
            align="center"
            content={CustomLegend}
          />

          {/* Top shaded area */}
          <Area
            yAxisId="left"
            type="linear"
            dataKey="base"
            stackId="a"
            stroke="none"
            fill="none"
            isAnimationActive={false}
          />
          <Area
            yAxisId="left"
            type="linear"
            dataKey="height"
            stackId="a"
            stroke="none"
            fill="url(#timeGainedGradient)"
            fillOpacity={1.0}
            isAnimationActive={false}
            name={CHART_STRINGS.TIME_GAINED}
          />

          <ReferenceArea
            yAxisId="left"
            x1={parseInt(dialysisInfo.noTreatmentDialysisAge.toFixed(0), 10)}
            x2={Math.min(
              parseInt(axisInfo.xMaxTicks.toFixed(0), 10),
              parseInt(axisInfo.xMax.toFixed(0), 10)
            )}
            y1={0}
            y2={CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET}
            fill="#FFDA7A"
            fillOpacity={1}
            stroke="none"
          >
            <Label content={<ProlongedDialysisLabel />} />
          </ReferenceArea>

          {/* Main trajectories */}
          <Line
            type="linear"
            dataKey="withTreatment"
            yAxisId="left"
            stroke="#00285B"
            strokeWidth={3}
            dot={false}
            name={CHART_STRINGS.LEGEND_INTERVENTION_LINE}
            isAnimationActive={false}
          />
          <Line
            type="linear"
            dataKey="withoutTreatment"
            yAxisId="left"
            stroke="#00285B"
            strokeDasharray="6 4"
            strokeWidth={2}
            dot={false}
            name={CHART_STRINGS.LEGEND_NO_INTERVENTION_LINE}
            isAnimationActive={false}
          />

          {/* Arrow Annotations */}
          <ReferenceDot
            yAxisId="left"
            x={dialysisInfo.treatmentInitiated}
            y={yAtInitiation}
            r={0}
            isFront
            label={<Label content={<TreatmentInitiationLabel />} />}
          />
          <ReferenceLine
            yAxisId="left"
            y={CHART_KEY_NUMERICAL_CONSTS.DIALYSIS_ONSET}
            stroke="white"
            strokeDasharray="5 5"
          >
            <Label content={<ApproximateDialysisLabel />} />
          </ReferenceLine>
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

DynamicChart.propTypes = {
  egfrInitial: PropTypes.number.isRequired,
  age: PropTypes.number.isRequired,
  risk: PropTypes.string.isRequired,
  treatment: PropTypes.string.isRequired,
};

export default DynamicChart;
